<script lang="ts" setup>
import { formatNumber } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useAtaTableUnderRemittanceLogic,
  type UseAtaTableUnderRemittanceLogicProps,
} from './logic/useAtaTableUnderRemittanceLogic';

const props = defineProps<UseAtaTableUnderRemittanceLogicProps>();
const { state, action } = useAtaTableUnderRemittanceLogic(props);
const { t } = useI18n();

const totalAmount = computed(() =>
  state.atas.reduce((sum, ata) => sum + (ata.amount ?? 0), 0),
);

function formatDate(date?: Date): string {
  if (!date) return '–';
  return date.toLocaleDateString('sv-SE');
}
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">{{ t('ata.table.underRemittanceTitle') }}</p>
    </div>
    <div class="card-content p-0">
      <table class="table is-fullwidth is-striped is-hoverable mb-0">
        <thead>
          <tr>
            <th class="col-number">#</th>
            <th>{{ t('ata.table.name') }}</th>
            <th class="col-date">{{ t('ata.table.sentDate') }}</th>
            <th class="col-date">{{ t('ata.table.deadlineDate') }}</th>
            <th class="col-priority">{{ t('ata.table.priorityShort') }}</th>
            <th class="col-amount">{{ t('ata.table.amount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ata in state.atas" :key="ata.number">
            <td class="col-number ata-number" @click="action.select(ata)">
              {{ t('ata.nameSerialNr', { serialNumber: ata.number }) }}
            </td>
            <td>{{ ata.name }}</td>
            <td class="col-date">{{ formatDate(ata.sentDate) }}</td>
            <td class="col-date" :class="{ 'has-text-danger': !ata.deadLine }">
              {{ formatDate(ata.deadLine) }}
            </td>
            <td class="col-priority">
              <span v-if="!ata.priority" class="priority-badge priority-badge--none">–</span>
              <span v-else class="priority-badge" :data-level="ata.priority">{{ ata.priority }}</span>
            </td>
            <td class="col-amount">{{ formatNumber(ata.amount) }} kr</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="summary-row">
            <td colspan="5">{{ t('sum') }}</td>
            <td class="col-amount">{{ formatNumber(totalAmount) }} kr</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped>
.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.table th,
.table td {
  padding: 0.75rem 1rem;
}

.col-number {
  width: 6rem;
}

.col-date {
  width: 10rem;
}

.col-priority {
  width: 5rem;
  text-align: center;
}

.col-amount {
  width: 11rem;
  text-align: right !important;
}

tfoot .summary-row td {
  font-weight: 700;
  border-top: 2px solid #ddd;
}

.ata-number {
  cursor: pointer;
  color: var(--primary-color);
  font-weight: 600;
}

.ata-number:hover {
  text-decoration: underline;
}

.muted {
  color: #888;
  font-size: 0.85rem;
}

.priority-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  font-size: 0.78rem;
  font-weight: 800;
  background: #f0f0f0;
  color: #555;
}

.priority-badge[data-level='1'] {
  background: #e74c3c;
  color: #fff;
}

.priority-badge[data-level='2'] {
  background: #e67e22;
  color: #fff;
}

.priority-badge[data-level='3'] {
  background: #f1c40f;
  color: #7a6000;
}

.priority-badge[data-level='4'] {
  background: #27ae60;
  color: #fff;
}

.priority-badge--none {
  background: #e0e0e0;
  color: #aaa;
}

@media (max-width: 900px) {
  .table th:nth-child(3),
  .table td:nth-child(3) {
    display: none;
  }

  .col-priority {
    width: 2.5rem;
  }

  .col-amount {
    width: 7rem;
  }
}
</style>
